import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {ForkygramPostData} from 'types'
import {clearUserCache, showSnackbar, useDefaultState, useHistory} from 'utils'
import {ListLazy} from 'common/components'
import {useSelector} from 'lib/redux'
import {TemplateProfileDetailItem} from '../ProfileDetailItem'
import {TemplateProfileDetailContentProps} from './TemplateProfileDetailContentProps'
import TemplateProfileDetailContentSkeleton from './TemplateProfileDetailContentSkeleton'

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
`

export default function TemplateProfileDetailContent({
  selectedItemId,
  stateData,
  stateEdit: stateEditBase,
  hideBottomNavbar,
  initialPage,
  isFeed,
  self,
  loadData,
  stateVideoPlaying,
  stateMute,
  stateVideoPause,
}: TemplateProfileDetailContentProps) {
  const {translate} = useTranslation()
  const {username} = useSelector('user') || {}
  const [data, setData] = stateData
  const stateEdit = useDefaultState(undefined, stateEditBase)
  const [scrollPos, setScrollPos] = useState(0)
  const setEdit = stateEdit[1]
  const history = useHistory()
  const setVideoPlaying = stateVideoPlaying[1]

  const handleNavigateProfile = useCallback(() => {
    history.push('giftshop_profile', {self: true}, username)
  }, [history, username])

  const handleDelete = useCallback(
    (item: ForkygramPostData) => {
      requestData('forkygram_delete_post', {
        actionType: 'execute',
        useDefaultMessage: true,
        data: {id: item.id},
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            setData((prev) => prev.filter((post) => post.id !== item.id))
            clearUserCache('post')
            if (data.length > 1) {
              setData((prev) => prev.filter((val) => val.id !== item.id))
            } else {
              handleNavigateProfile()
            }
          }
        },
      })
    },
    [data.length, handleNavigateProfile, setData],
  )

  const handlePin = useCallback(
    (id: string, isPinned: boolean) => {
      isPinned
        ? requestData('forkygram_delete_pin_posts', {
            actionType: 'execute',
            useDefaultMessage: true,
            data: {id},
            onRequestSuccess: ({status}) => {
              if (status === 200) {
                showSnackbar(translate('forkygram:postUnpinSuccess'))
                handleNavigateProfile()
              }
            },
          })
        : requestData('forkygram_post_pin_posts', {
            actionType: 'execute',
            useDefaultMessage: true,
            data: {id},
            onRequestSuccess: ({status}) => {
              if (status === 200) {
                showSnackbar(translate('forkygram:postPinSuccess'))
                handleNavigateProfile()
              } else if (status === 400) {
                showSnackbar(translate('forkygram:postPinFailed'))
              }
            },
          })
    },
    [handleNavigateProfile, translate],
  )

  const selectedComponentRef = useCallback((node: HTMLDivElement) => {
    node &&
      node.scrollIntoView({
        block: 'start',
        inline: 'nearest',
      })
  }, [])

  const handleFollow = useCallback(
    (memberId: string) => {
      setData((prev) =>
        prev.map((x) =>
          x.collaborator_members.some((m) => m.member_id === memberId)
            ? {
                ...x,
                collaborator_members: x.collaborator_members.map((m) =>
                  m.member_id === memberId ? {...m, is_followed: true} : m,
                ),
              }
            : x,
        ),
      )
    },
    [setData],
  )

  const handleRemoveCollaborator = useCallback(
    (item: ForkygramPostData, memberId: string) => {
      setData((prev) =>
        prev.map((x) =>
          x.id === item.id
            ? {
                ...x,
                collaborator_members: x.collaborator_members.filter(
                  (m) => m.member_id !== memberId,
                ),
              }
            : x,
        ),
      )
    },
    [setData],
  )

  const handleRenderItem = useCallback(
    (item: ForkygramPostData) => (
      <TemplateProfileDetailItem
        componentRef={
          selectedItemId && selectedItemId === item.id
            ? selectedComponentRef
            : null
        }
        data={item}
        onDelete={() => handleDelete(item)}
        onEdit={() => setEdit(item)}
        onPin={(pinnedId, isPinned) => handlePin(pinnedId, isPinned)}
        hideBottomNavbar={hideBottomNavbar}
        isFeed={isFeed}
        scrollPosition={scrollPos}
        index={data.indexOf(item)}
        self={self}
        stateVideoPause={stateVideoPause}
        stateMute={stateMute}
        setVideoPlaying={setVideoPlaying}
        onReported={(reportedId) =>
          setData((prev) => prev.filter((post) => post.id !== reportedId))
        }
        onFollowed={handleFollow}
        onCollabRemoved={(memberId) => handleRemoveCollaborator(item, memberId)}
      />
    ),
    [
      data,
      handleDelete,
      handleFollow,
      handlePin,
      handleRemoveCollaborator,
      hideBottomNavbar,
      isFeed,
      scrollPos,
      selectedComponentRef,
      selectedItemId,
      self,
      setData,
      setEdit,
      setVideoPlaying,
      stateMute,
      stateVideoPause,
    ],
  )

  const handleRenderList = useMemo(
    () => (
      <ListLazy
        cache={self ? 'post' : undefined}
        onScrollList={(e) => setScrollPos(e.currentTarget.scrollTop)}
        stateData={stateData}
        keyExtractor={(item) => item.id}
        listInitializeElement={<TemplateProfileDetailContentSkeleton />}
        renderItem={handleRenderItem}
        loadData={loadData}
        scrollbar={false}
        initialPage={initialPage}
        scrollSnapType="y mandatory"
      />
    ),
    [handleRenderItem, initialPage, loadData, self, stateData],
  )

  return <StyledContainer>{handleRenderList}</StyledContainer>
}
